import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { invalidateFor } from "@/lib/cache-registry";
import {
  updateWithOptimisticLock,
  OptimisticLockConflictError,
} from "@/lib/optimistic-lock";

/**
 * Rolle-kolonner på `projects` som peker til en bruker. Holder lista
 * smal slik at typesystemet fanger feilstavede kolonnenavn.
 */
export type ProjectRoleColumn = "production_lead_id" | "advisor_id";

const ROLE_LABEL: Record<ProjectRoleColumn, string> = {
  production_lead_id: "prosjektleder",
  advisor_id: "kunderådgiver",
};

interface UpdateRoleInput {
  projectId: string;
  column: ProjectRoleColumn;
  /** null = nullstill rollen */
  userId: string | null;
  /** VIB-757: project.updated_at fra siste hentet data. */
  expectedUpdatedAt: string;
}

/**
 * Oppdater en rolle-kolonne på et prosjekt (Prosjektleder eller
 * Kunderådgiver). Brukes av EditableProjectUserField. Ved konflikt
 * vises egen toast og project-detail-cachen invalideres.
 */
export function useUpdateProjectRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, column, userId, expectedUpdatedAt }: UpdateRoleInput) => {
      await updateWithOptimisticLock(
        "projects",
        projectId,
        { [column]: userId },
        expectedUpdatedAt,
      );
    },
    onSuccess: async (_data, vars) => {
      await invalidateFor(qc, "projects", { projectId: vars.projectId });
    },
    onError: (err, vars) => {
      if (err instanceof OptimisticLockConflictError) {
        toast.error(
          "En annen bruker har endret prosjektet. Last inn på nytt og prøv igjen.",
        );
        void invalidateFor(qc, "projects", { projectId: vars.projectId });
      } else {
        toast.error(`Kunne ikke endre ${ROLE_LABEL[vars.column]}: ${(err as Error).message}`);
      }
    },
  });
}
